import { useState, useCallback } from "react";
import { Edit, Trash2 } from "react-feather";
import { useNavigate } from "react-router-dom";
import { api } from "../../config/Api";
import {
    ActivateDeactivateIndustryInterface,
    IndustryInterface,
    IndustryTableInterface,
} from "../../helper/interface";
import usePathName from "../../hooks/usePathName";
import useTable from "../../hooks/useTable";
import CustomTable from "../table/CustomTable";
import { useDispatch } from "react-redux";
import SearchForm from "../table/SearchForm";
import DeleteModal from "../modal/DeleteModal";
import { onActivateDeactivateIndustry, onDeleteIndustry } from "../../service/industry";
import { Tooltip } from "react-tooltip";

const Industry = () => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [industry, setIndustry] = useState<IndustryInterface | null>(null);
    const dispatch = useDispatch();
    usePathName([{ pathName: "Industry", href: "/position" }]);

    const industryTable = useTable<IndustryTableInterface>("industry", api.admin.getIndustry);

    const onDeleteClick = useCallback((record: IndustryTableInterface) => {
        setIndustry(record);
        setIsOpen(true);
    }, []);

    const industryCols = [
        {
            title: "#",
            key: "industryId",
            render: (record: IndustryTableInterface, srNo: number) => {
                return <span>{srNo}</span>;
            },
        },
        {
            title: "INDUSTRY",
            key: "name",
            sortable: true,
            render: (record: IndustryTableInterface) => {
                return <span>{record.name}</span>;
            },
        },
        {
            title: "POSITIONS",
            key: "positionList",
            render: (record: IndustryTableInterface) => {
                const positions = (record.positionList ?? [])
                    .map((ele) => ele.positionDetails?.name)
                    .filter(Boolean)
                    .join(", ");
                return (
                    <div className="max-w-xs whitespace-normal mx-auto text-left">{positions || "-"}</div>
                );
            },
        },
        {
            title: "STATUS",
            key: "isActive",
            render: (record: IndustryTableInterface) => {
                const payload = {
                    industryId: record.industryId,
                    isActive: !record.isActive,
                };
                return (
                    <div className="form-switch form-check flex justify-center flex-col">
                        <input
                            id="isActive"
                            name="isActive"
                            onChange={() => onActivateDeactivate(payload)}
                            checked={record.isActive}
                            className={"form-check-input"}
                            type="checkbox"
                        />
                    </div>
                );
            },
        },
        {
            title: "ACTION",
            key: "action",
            render: (record: IndustryTableInterface) => {
                return (
                    <>
                        <div className="flex justify-center items-center text-primary">
                            <Tooltip id="simpleTooltipEditDeleteIndustry" place="top" />
                            <div
                                className="flex items-center mr-3 cursor-pointer"
                                data-tooltip-id="simpleTooltipEditDeleteIndustry"
                                data-tooltip-content={"Edit industry"}
                                onClick={() => navigate(`/industry/edit/${record.industryId}`)}
                            >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                            </div>
                            <div
                                className="flex items-center text-danger cursor-pointer"
                                data-tooltip-id="simpleTooltipEditDeleteIndustry"
                                data-tooltip-content={"Delete industry"}
                                onClick={() => onDeleteClick(record)}
                            >
                                <Trash2 className="w-4 h-4 mr-1" /> Delete
                            </div>
                        </div>
                    </>
                );
            },
        },
    ];

    const onDelete = () => {
        if (industry?.industryId) {
            onDeleteIndustry(industry.industryId, dispatch, industryTable.fetchApi);
            setIsOpen(false);
        }
    };

    const onActivateDeactivate = (payload: ActivateDeactivateIndustryInterface) => {
        onActivateDeactivateIndustry(payload, dispatch, industryTable.fetchApi);
    };

    const handleCancel = () => {
        setIsOpen(false);
    };

    return (
        <>
            <DeleteModal
                handleCancel={handleCancel}
                isOpen={isOpen}
                onDelete={onDelete}
                subTitle="Do you really want to delete this industry?"
            />

            <div className="grid grid-cols-12 gap-6 mt-5">
                <CustomTable
                    cols={industryCols}
                    data={industryTable.tableData ?? []}
                    tableKey="industryId"
                    reload={industryTable.fetchApi}
                    currentOrder={industryTable.currentOrder}
                    error={industryTable.error}
                    loading={industryTable.loading}
                    tableOffset={industryTable.tableOffset}
                    onSetOrderBy={industryTable.onSetOrderBy}
                    component={
                        <>
                            <SearchForm resetSearch={industryTable.resetSearch} onSearch={industryTable.onSearch} searchPlaceHolder="Search by industry" />
                            <div className="flex mt-5 sm:mt-0 mr-2 sm:mr-0">
                                <Tooltip id="simpleTooltipIndustry" place="top" />
                                <button
                                    className="btn btn-outline-primary w-full md:w-1/2 sm:w-auto"
                                    data-tooltip-id="simpleTooltipIndustry"
                                    data-tooltip-content="Add new industry"
                                    onClick={() => {
                                        navigate("/industry/add");
                                    }}
                                >
                                    Add new
                                </button>
                            </div>
                        </>
                    }
                />
            </div>
        </>
    );
};

export default Industry;
